import { MESSAGES } from 'src/modules/common/constants.ts';
import { Modal } from 'src/modules/common/components/Modal/Modal.tsx';
import { ModalHeader } from 'src/modules/common/components/Modal/ModalHeader.tsx';
import { ModalBody } from 'src/modules/common/components/Modal/ModalBody.tsx';
import { ModalFooter } from 'src/modules/common/components/Modal/ModalFooter.tsx';
import { Button } from 'src/modules/common/components/Button/Button.tsx';
import { I18n } from 'src/modules/common/components/I18n/I18n.tsx';
import { useCallback, useContext, useState } from 'react';
import { ConfigContext } from 'src/configContext.ts';
import { CalcV100 } from 'src/modules/user-input/components/CalcV100.tsx';
import { CalcMaxDP } from 'src/modules/user-input/components/CalcMaxDP.tsx';
import { LocalesEnum, RegionsEnum } from 'src/generated/operations.ts';
import { AllowedUnits } from 'src/modules/user-input/calcV100/unitsMapping.ts';
import {
    VolumetricFlowCalculatorDataState,
    VolumetricFlowCalculatorInput,
} from 'src/modules/user-input/calcVolumetricFlow/types.ts';
import { targetUnits } from 'src/modules/user-input/targetUnits.ts';
import { Unit } from 'uom';
import { CalcFtHead } from 'src/modules/user-input/components/CalcFtHead.tsx';
import { CalcKindEnum } from 'shared/enums/CalcKindEnum';

export type MODAL_CALCULATOR_PROPS = {
    title: string;
    allowedUnits: AllowedUnits;
    targetUnit: Unit.Unit<unknown>;
    handleModalValueChange: (currentValue:string) => void;
    currentLocale: LocalesEnum;
    currentRegion: RegionsEnum;
    uuid: string;
    kind: CalcKindEnum;
};

export type HandleModalTransferEvent = {
    value: string;
    unit: Unit.Unit<unknown>;
    additionals?: Partial<VolumetricFlowCalculatorInput>;
}

export type ModalState = {
    field: keyof VolumetricFlowCalculatorDataState;
    unit: Unit.Unit<unknown>;
} | null;

type FlowCalculatorModalsProps = {
    allowedUnits: AllowedUnits;
    modalsState: ModalState;
    kind: CalcKindEnum;
    handleModalClose: () => void;
    handleModalTransfer: (detail: HandleModalTransferEvent) => void;
}

export const FlowCalculatorModals = ({
    modalsState,
    kind,
    handleModalClose,
    handleModalTransfer,
    allowedUnits,
}: FlowCalculatorModalsProps) => {
    const config = useContext(ConfigContext);
    const [currentValue, setCurrentValue] = useState('');
    const handleModalValueChange = useCallback((value :string) => {
        setCurrentValue(value);
    }, [setCurrentValue]);

    return [
        {
            name: 'v100',
            title: MESSAGES.CalcV100Title,
            description: MESSAGES.CalcV100TitleDescr,
            titleResult: MESSAGES.CalcV100TitleResult,
            kind,
            calculator: CalcV100,
            unit: targetUnits.volumetricFlow,
        },
        {
            name: 'mass',
            title: MESSAGES.CalcMassFlowTitle,
            description: MESSAGES.CalcMassFlowTitleDescr,
            titleResult: MESSAGES.CalcMassFlowTitleResult,
            kind,
            // @TODO implement proper calc
            calculator: CalcV100,
            unit: targetUnits.massVolumetricFlow,
        },
        ...(config.userSettings.currentRegion === RegionsEnum.Am ? [{
            name: 'maxDp',
            title: MESSAGES.CalcMaxDPTitle,
            description: MESSAGES.CalcMaxDPTitleDescr,
            titleResult: MESSAGES.CalcMaxDPTitleResult,
            kind,
            calculator: CalcFtHead,
            unit: targetUnits.dpv100,
        }] : []),
        {
            name: 'inlet',
            title: MESSAGES.CalcInletTitle,
            description: MESSAGES.CalcInletDescr,
            titleResult: MESSAGES.CalcInletTitleResult,
            kind,
            calculator: CalcMaxDP,
            unit: targetUnits.dpv100,
        },
        {
            name: 'outlet',
            title: MESSAGES.CalcOutletTitle,
            description: MESSAGES.CalcOutletDescr,
            titleResult: MESSAGES.CalcOutletTitleResult,
            kind,
            calculator: CalcMaxDP,
            unit: targetUnits.dpv100,
        }]
        .map((item) => (
            <Modal
                key={item.name}
                opened={modalsState?.field === item.name}
                handleClose={handleModalClose}
                root={config.rootElement}
            >
                <ModalHeader
                    handleClose={handleModalClose}
                    title={item.title}
                ><I18n messageId={item.description}/>
                </ModalHeader>
                <ModalBody>
                    {item.calculator({
                        title: item.titleResult,
                        currentLocale: config.userSettings.currentLocale,
                        currentRegion: config.userSettings.currentRegion,
                        handleModalValueChange,
                        allowedUnits,
                        targetUnit: modalsState?.unit || item.unit,
                        uuid: item.name,
                        kind,
                    })}
                </ModalBody>
                <ModalFooter>
                    <Button
                        kind="secondary"
                        fullWidth={true}
                        onClick={handleModalClose}
                    >
                        <I18n messageId={MESSAGES.CANCEL}/>
                    </Button>
                    <Button
                        fullWidth={true}
                        onClick={() => {
                            handleModalTransfer({
                                value: currentValue,
                                unit: modalsState?.unit || item.unit,
                            });
                        }}
                    >
                        <I18n messageId={MESSAGES.apply}/>
                    </Button>
                </ModalFooter>
            </Modal>
        ));
};
